import React from 'react';
import PropTypes from 'prop-types';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import * as Styled from './styles';

/**
 * Clickable column header that toggles the sort direction of `sortKey`, with an arrow showing the
 * current direction. Shared by every new-design list table so the gesture and the a11y wiring stay
 * identical (pair it with the `aria-sort` attribute on the <th>, which <DataTable /> forwards).
 *
 * `handleSort` is the curried handler from `utils/old/useSortTable`: handleSort(field)(currentOrder)
 * flips the direction — 1 -> -1, -1 -> 1, and an inactive column (0) starts at descending.
 *
 * Pass `showArrow={false}` when the header already sits next to a <SortMenu variant="cell" />: that
 * trigger renders the active direction itself, and two arrows in one header cell read as clutter.
 */
const SortableHeader = ({ label, sortKey, sortOption, handleSort, title, showArrow }) => {
  const isActive = sortOption.sortBy === sortKey;
  const order = isActive ? sortOption.sortOrder : 0;

  return (
    <Styled.SortHeader
      type="button"
      active={isActive}
      onClick={() => handleSort(sortKey)(order)}
      title={title}
      aria-label={`Sort by ${label}${isActive ? `, currently ${order === 1 ? 'ascending' : 'descending'}` : ''}`}
    >
      <span>{label}</span>
      {showArrow && (isActive && order === 1 ? <FiChevronUp /> : <FiChevronDown />)}
    </Styled.SortHeader>
  );
};

SortableHeader.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  sortOption: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.number }).isRequired,
  handleSort: PropTypes.func.isRequired,
  // Optional tooltip — e.g. to point at the arrow menu on columns with extra sub-field sorts.
  title: PropTypes.string,
  showArrow: PropTypes.bool,
};

SortableHeader.defaultProps = {
  title: undefined,
  showArrow: true,
};

export default SortableHeader;
